/**
 * Bank connectors API (TMMAPP-0024, design §9.5; provider-neutral since TMMAPP-0052, MM-D-104) —
 * wire types from `src/api/generated/`; normalized into the module's domain type.
 * `CONNECTOR_UNAVAILABLE` is rethrown as-is so callers can render the unavailable state. tmm-api
 * picks the provider: every link and repair starts at `link-session` and ends at `complete` (or,
 * for a Plaid repair, `sync`). The SPA never sees a provider access token.
 */
import { request } from './client';
import { ApiShapeError } from './errors';
import type {
  ConnectorAccountPatchRequest,
  ConnectorAccountView as WireAccount,
  ConnectorCompleteRequest,
  ConnectorInitialSync as WireInitialSync,
  ConnectorItemView as WireItem,
  ConnectorLinkSession as WireLinkSession,
  ConnectorSyncQueued as WireSync,
} from './generated';
import type { AccountSubtype, AccountType } from './accounts';

export type ConnectorStatus =
  'ACTIVE' | 'LOGIN_REQUIRED' | 'PENDING_EXPIRATION' | 'REVOKED' | 'REMOVED';

/**
 * Whether a connection can be re-authenticated ("Reconnect"). tmm-api answers anything else with
 * 409 `CONNECTOR_NOT_REAUTHABLE` (TMMAPI-0096), so no path starts a repair outside these two.
 */
export function isReauthable(status: string): boolean {
  return status === 'LOGIN_REQUIRED' || status === 'PENDING_EXPIRATION';
}

/**
 * The bank-data provider behind a connection — `'PLAID'` or `'STRIPE_FC'` today — kept as received
 * so a later one renders neutrally. Never shown to the person (no provider branding).
 */
export type ConnectorProvider = string;

export interface ConnectorAccount {
  providerAccountId: string;
  name: string;
  officialName: string | null;
  mask: string | null;
  providerType: string | null;
  providerSubtype: string | null;
  currency: string | null;
  lastBalanceMinor: number | null;
  balanceAsOf: string | null;
  accountId: number | null;
}

export interface ConnectorItem {
  id: number;
  provider: ConnectorProvider | null;
  institutionName: string;
  status: string;
  consentExpiresAt: string | null;
  lastSyncedAt: string | null;
  newAccountsAvailable: boolean;
  accounts: ConnectorAccount[];
  /** First sync after link; null when the wire omits it. */
  initialSync: ConnectorInitialSync | null;
}

export type ConnectorInitialSyncState = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';

export interface ConnectorInitialSync {
  state: ConnectorInitialSyncState | null;
  importedCount: number | null;
}

/** `POST /link-session`: which provider's window to open, and with what (memory only). */
export type LinkSession =
  | { provider: 'PLAID'; linkToken: string; expiresAt: string | null }
  | {
      provider: 'STRIPE_FC';
      clientSecret: string;
      publishableKey: string;
      sessionRef: string;
      expiresAt: string | null;
    };

export interface ConnectorSyncQueued {
  itemId: number | null;
  queuedAt: string | null;
}

export interface ConnectorCreateAccount {
  name: string;
  type: AccountType;
  subtype: AccountSubtype;
  currency: string;
}

export interface ConnectorMapping {
  providerAccountId: string;
  accountId?: number;
  create?: ConnectorCreateAccount;
}

export interface ConnectorCompleteBody {
  provider: 'PLAID' | 'STRIPE_FC';
  /** PLAID: from Link's onSuccess. */
  publicToken?: string;
  /** STRIPE_FC: from the link session. */
  sessionRef?: string;
  /** Re-authenticates this connection (a Stripe repair); omitted for a new link. */
  itemId?: number;
  mappings: ConnectorMapping[];
}

/**
 * Every `ConnectorAccountView` field is optional in the contract; an account without a
 * `providerAccountId` can't be mapped and is dropped.
 */
export function normalizeConnectorAccount(w: WireAccount): ConnectorAccount | null {
  if (w.providerAccountId == null || w.providerAccountId.length === 0) return null;
  return {
    providerAccountId: w.providerAccountId,
    name: w.name ?? '',
    officialName: w.officialName ?? null,
    mask: w.mask ?? null,
    providerType: w.providerType ?? null,
    providerSubtype: w.providerSubtype ?? null,
    currency: w.currency ?? null,
    lastBalanceMinor: w.lastBalanceMinor ?? null,
    balanceAsOf: w.balanceAsOf ?? null,
    accountId: w.accountId ?? null,
  };
}

/** An Item; `null` (dropped) without an id (optional in the contract). */
export function normalizeConnectorItem(w: WireItem): ConnectorItem | null {
  if (w.id == null) return null;
  return {
    id: w.id,
    provider: w.provider ?? null,
    institutionName: w.institutionName ?? '',
    status: w.status ?? '',
    consentExpiresAt: w.consentExpiresAt ?? null,
    lastSyncedAt: w.lastSyncedAt ?? null,
    newAccountsAvailable: w.newAccountsAvailable ?? false,
    accounts: (w.accounts ?? []).flatMap((row) => {
      const account = normalizeConnectorAccount(row);
      return account ? [account] : [];
    }),
    initialSync: w.initialSync == null ? null : normalizeInitialSync(w.initialSync),
  };
}

const INITIAL_SYNC_STATES: ReadonlySet<string> = new Set(['PENDING', 'RUNNING', 'DONE', 'FAILED']);

function normalizeInitialSync(w: WireInitialSync): ConnectorInitialSync {
  const state = w.state != null && INITIAL_SYNC_STATES.has(w.state) ? w.state : null;
  return { state, importedCount: w.importedCount ?? null };
}

export function normalizeConnectorItems(value: readonly WireItem[]): ConnectorItem[] {
  return value.flatMap((row) => {
    const item = normalizeConnectorItem(row);
    return item ? [item] : [];
  });
}

function requiredItem(wire: WireItem): ConnectorItem {
  const item = normalizeConnectorItem(wire);
  if (!item) throw new ApiShapeError('connector.id is required');
  return item;
}

function requiredAccount(wire: WireAccount): ConnectorAccount {
  const account = normalizeConnectorAccount(wire);
  if (!account) throw new ApiShapeError('connector.providerAccountId is required');
  return account;
}

function requiredString(value: string | undefined, field: string): string {
  if (value == null || value.length === 0) {
    throw new ApiShapeError(`linkSession.${field} is required`);
  }
  return value;
}

/**
 * Every `ConnectorLinkSession` field is optional in the contract: a field missing for its
 * branch, or an unknown provider, is an `ApiShapeError`.
 */
export function normalizeLinkSession(w: WireLinkSession): LinkSession {
  const expiresAt = w.expiresAt ?? null;
  if (w.provider === 'PLAID') {
    return { provider: 'PLAID', linkToken: requiredString(w.linkToken, 'linkToken'), expiresAt };
  }
  if (w.provider === 'STRIPE_FC') {
    return {
      provider: 'STRIPE_FC',
      clientSecret: requiredString(w.clientSecret, 'clientSecret'),
      publishableKey: requiredString(w.publishableKey, 'publishableKey'),
      sessionRef: requiredString(w.sessionRef, 'sessionRef'),
      expiresAt,
    };
  }
  throw new ApiShapeError('linkSession.provider is unknown');
}

export function normalizeSyncQueued(w: WireSync): ConnectorSyncQueued {
  return { itemId: w.itemId ?? null, queuedAt: w.queuedAt ?? null };
}

export const connectorsApi = {
  /** GET /v1/me/connectors. */
  async list(): Promise<ConnectorItem[]> {
    return normalizeConnectorItems(await request<WireItem[]>('GET', '/v1/me/connectors'));
  },
  /** POST /v1/me/connectors/link-session. `itemId` re-authenticates that connection. */
  async linkSession(itemId?: number): Promise<LinkSession> {
    const json: { itemId?: number } = {};
    if (itemId !== undefined) json.itemId = itemId;
    return normalizeLinkSession(
      await request<WireLinkSession>('POST', '/v1/me/connectors/link-session', { json }),
    );
  },
  /** POST /v1/me/connectors/complete — the connection as the server now holds it. */
  async complete(body: ConnectorCompleteBody): Promise<ConnectorItem> {
    const json: ConnectorCompleteRequest = {
      provider: body.provider,
      ...(body.publicToken !== undefined ? { publicToken: body.publicToken } : {}),
      ...(body.sessionRef !== undefined ? { sessionRef: body.sessionRef } : {}),
      ...(body.itemId !== undefined ? { itemId: body.itemId } : {}),
      mappings: body.mappings.map((mapping) => ({
        providerAccountId: mapping.providerAccountId,
        ...(mapping.accountId !== undefined ? { accountId: mapping.accountId } : {}),
        ...(mapping.create !== undefined ? { create: mapping.create } : {}),
      })),
    };
    return requiredItem(await request<WireItem>('POST', '/v1/me/connectors/complete', { json }));
  },
  /**
   * PATCH /v1/me/connectors/{id}/accounts/{providerAccountId} — a single-purpose set body:
   * `accountId` maps the provider account, `null` unmaps it.
   */
  async mapAccount(
    itemId: number,
    providerAccountId: string,
    accountId: number | null,
  ): Promise<ConnectorAccount> {
    const json: ConnectorAccountPatchRequest = { accountId };
    return requiredAccount(
      await request<WireAccount>(
        'PATCH',
        `/v1/me/connectors/${itemId}/accounts/${encodeURIComponent(providerAccountId)}`,
        { json },
      ),
    );
  },
  /** POST /v1/me/connectors/{id}/sync — queued, not synchronous. */
  async sync(itemId: number): Promise<ConnectorSyncQueued> {
    return normalizeSyncQueued(await request<WireSync>('POST', `/v1/me/connectors/${itemId}/sync`));
  },
  /** DELETE /v1/me/connectors/{id}. */
  async remove(itemId: number): Promise<void> {
    await request<unknown>('DELETE', `/v1/me/connectors/${itemId}`);
  },
};
